import React, { useEffect, useState } from 'react';
import {
    Button,
    Select,
    Row,
    Col,
    Space,
    Spin,
    Table,
    message,
    Tag,
} from 'antd';
import {
    SaveOutlined,
    EditOutlined,
    DeleteOutlined,
    CloseOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import AppLayout from '../layout/AppLayout';
import { useCourseApi } from '../../api/courses/courses';
import { TLessonProject } from '../../models/project/project';
import { TLesson } from '../../models/courses/lesson';
import { TSubject, TCourse, TChapter, TEpic } from '../../models/courses/course';
import { useReferential } from '../../context/ReferentialContext';
import { CODIFICATION_TYPE } from '../../constant/codification';
import { useProjectApi } from '../../api/projects/projects';

const { Option } = Select;

interface IGroupedData {
    project_type_id: number;
    lesson_ids: number[];
    key: number;
}

const LessonProjectList: React.FC = () => {
    const { t } = useTranslation();
    const {
        getAllLessons,
        getSubjects,
        getCoursesBySubjectId,
        getChaptersByCourseId,
        getEpicsByChapterId,
        getLessonsByEpicId
    } = useCourseApi();
    const {
        getAllLessonProjects,
        createLessonProject,
        updateLessonProject,
        deleteLessonProject
    } = useProjectApi();

    const { getCodificationsByCodeType } = useReferential();
    const projectTypeCodifications = getCodificationsByCodeType(
        CODIFICATION_TYPE.PROJECT_TYPE
    );

    // États pour les associations lesson_project et toutes les leçons (pour affichage)
    const [lessonProjects, setLessonProjects] = useState<TLessonProject[]>([]);
    const [allLessons, setAllLessons] = useState<TLesson[]>([]);
    const [loading, setLoading] = useState<boolean>(false);

    // États pour le sélecteur en cascade
    const [selectedProjectType, setSelectedProjectType] = useState<number | null>(
        null
    );
    const [subjects, setSubjects] = useState<TSubject[]>([]);
    const [selectedSubject, setSelectedSubject] = useState<number | null>(null);
    const [courses, setCourses] = useState<TCourse[]>([]);
    const [selectedCourse, setSelectedCourse] = useState<number | null>(null);
    const [chapters, setChapters] = useState<TChapter[]>([]);
    const [selectedChapter, setSelectedChapter] = useState<number | null>(null);
    const [epics, setEpics] = useState<TEpic[]>([]);
    const [selectedEpic, setSelectedEpic] = useState<number | null>(null);
    const [lessons, setLessons] = useState<TLesson[]>([]);
    const [selectedLessonIds, setSelectedLessonIds] = useState<number[]>([]);

    // État pour la gestion de l'édition
    const [editingKey, setEditingKey] = useState<number | null>(null);

    // --- Exemple : on suppose que TLessonProject a un champ `condition` qu'on veut modifier :
    // On stocke la liste TLessonProject de la ligne en cours d'édition
    const [editingLessonProjects, setEditingLessonProjects] =
        useState<TLessonProject[]>([]);

    useEffect(() => {
        setLoading(true);
        Promise.all([getAllLessonProjects(), getAllLessons(), getSubjects()])
            .then(([lpResp, lessonsResp, subjectsResp]) => {
                setLessonProjects(lpResp.data || []);
                setAllLessons(lessonsResp.data || []);
                setSubjects(subjectsResp.data || []);
            })
            .catch((err) => message.error(err.message))
            .finally(() => setLoading(false));
        // eslint-disable-next-line
    }, []);

    // Au changement de subject, charger les cours associés
    useEffect(() => {
        if (selectedSubject) {
            getCoursesBySubjectId(selectedSubject)
                .then((resp) => setCourses(resp.data || []))
                .catch((err) => message.error(err.message));
        } else {
            setCourses([]);
        }
        setSelectedCourse(null);
        setSelectedChapter(null);
        setSelectedEpic(null);
        setSelectedLessonIds([]);
        // eslint-disable-next-line
    }, [selectedSubject]);

    // Au changement de course, charger les chapitres associés
    useEffect(() => {
        if (selectedCourse) {
            getChaptersByCourseId(selectedCourse)
                .then((resp) => setChapters(resp.data || []))
                .catch((err) => message.error(err.message));
        } else {
            setChapters([]);
        }
        setSelectedChapter(null);
        setSelectedEpic(null);
        setSelectedLessonIds([]);
        // eslint-disable-next-line
    }, [selectedCourse]);

    // Au changement de chapter, charger les epics associés
    useEffect(() => {
        if (selectedChapter) {
            getEpicsByChapterId(selectedChapter)
                .then((resp) => setEpics(resp.data || []))
                .catch((err) => message.error(err.message));
        } else {
            setEpics([]);
        }
        setSelectedEpic(null);
        setSelectedLessonIds([]);
        // eslint-disable-next-line
    }, [selectedChapter]);

    // Au changement de epic, charger les leçons associés
    useEffect(() => {
        if (selectedEpic) {
            getLessonsByEpicId(selectedEpic)
                .then((resp) => setLessons(resp.data || []))
                .catch((err) => message.error(err.message));
        } else {
            setLessons([]);
        }
        setSelectedLessonIds([]);
        // eslint-disable-next-line
    }, [selectedEpic]);

    // Fonction pour sauvegarder une nouvelle association
    const handleSaveNew = () => {
        if (!selectedProjectType || !selectedLessonIds.length) {
            message.error(t('message.value_required'));
            return;
        }

        const newLessonProjects = selectedLessonIds.map((lessonId) => ({
            id: 0, // Temporary ID for creation
            project_type_id: selectedProjectType,
            lesson_id: lessonId,
            condition: '' // Valeur par défaut
        }));

        Promise.all(newLessonProjects.map((lp) => createLessonProject(lp)))
            .then(() => {
                message.success(t('message.savedSuccessfully'));
                return getAllLessonProjects();
            })
            .then((resp) => setLessonProjects(resp.data || []))
            .catch((err) => message.error(err.message));
    };

    // Fonction pour gérer l'édition
    const handleEdit = (record: IGroupedData) => {
        setEditingKey(record.key);
        // Récupérer les lesson_projects existants pour ce type de projet
        const existingLPs = lessonProjects.filter(
            (lp) => lp.project_type_id === record.project_type_id
        );
        setEditingLessonProjects(existingLPs);
    };

    // Fonction pour sauvegarder les modifications
    const handleSaveEdit = () => {
        if (!editingLessonProjects.length) return;

        Promise.all(
            editingLessonProjects.map((lp) => updateLessonProject(lp.id, lp))
        )
            .then(() => {
                message.success(t('message.savedSuccessfully'));
                setEditingKey(null);
                setEditingLessonProjects([]);
                return getAllLessonProjects();
            })
            .then((resp) => setLessonProjects(resp.data || []))
            .catch((err) => message.error(err.message));
    };

    // Fonction pour gérer les changements de condition
    const handleChangeCondition = (lessonId: number, value: string) => {
        setEditingLessonProjects((prev) =>
            prev.map((lp) =>
                lp.lesson_id === lessonId ? { ...lp, condition: value } : lp
            )
        );
    };

    // Grouper les données par type de projet
    const groupedData: IGroupedData[] = [];
    const projectTypeGroups = new Map<number, number[]>();

    lessonProjects.forEach((lp) => {
        if (!projectTypeGroups.has(lp.project_type_id)) {
            projectTypeGroups.set(lp.project_type_id, []);
        }
        projectTypeGroups.get(lp.project_type_id)!.push(lp.lesson_id);
    });

    projectTypeGroups.forEach((lessonIds, projectTypeId) => {
        groupedData.push({
            project_type_id: projectTypeId,
            lesson_ids: lessonIds,
            key: projectTypeId
        });
    });

    // Fonction pour rendre les tags de leçons
    const renderLessonTags = (lessonIds: number[], record: IGroupedData) => {
        return lessonIds.map((lessonId) => {
            const lesson = allLessons.find((l) => l.id === lessonId);
            const isEditing = record.key === editingKey;
            const editingLP = editingLessonProjects.find(
                (lp) => lp.lesson_id === lessonId
            );

            return (
                <Tag key={lessonId} style={{ marginBottom: 4 }}>
                    {lesson ? lesson.name : lessonId}
                    {/*
          // Exemple d'input pour modifier la condition
          <Input
            placeholder="Condition"
            value={editingLP?.condition || ''}
            onChange={(e) => handleChangeCondition(lessonId, e)}
            style={{ width: 100, marginLeft: 8 }}
          />
          */}
                </Tag>
            );
        });
    };

    // Définition des colonnes du tableau
    const columns = [
        {
            title: t('lessonProject.tableHeaders.projectType'),
            dataIndex: 'project_type_id',
            key: 'project_type_id',
            render: (id: number) => {
                const codif = projectTypeCodifications.find((c: any) => c.id === id);
                return codif ? codif.label : id;
            }
        },
        {
            title: t('lessonProject.tableHeaders.lessons'),
            dataIndex: 'lesson_ids',
            key: 'lesson_ids',
            render: (lessonIds: number[], record: IGroupedData) =>
                renderLessonTags(lessonIds, record)
        },
        {
            title: t('lessonProject.tableHeaders.action'),
            key: 'action',
            render: (_: any, record: IGroupedData) => {
                const isEditing = record.key === editingKey;
                if (isEditing) {
                    // Boutons "Enregistrer" / "Annuler"
                    return (
                        <Space>
                            <Button icon={<SaveOutlined />} onClick={handleSaveEdit}>
                                {t('lessonProject.save')}
                            </Button>
                            <Button
                                icon={<CloseOutlined />}
                                onClick={() => {
                                    setEditingKey(null);
                                    setEditingLessonProjects([]);
                                }}
                            >
                                {t('button.cancel')}
                            </Button>
                        </Space>
                    );
                }
                return (
                    <Space>
                        <Button icon={<EditOutlined />} onClick={() => handleEdit(record)}>
                            {t('button.edit')}
                        </Button>
                        <Button
                            icon={<DeleteOutlined />}
                            danger
                            onClick={() => {
                                // Exemple de suppression : on supprime toutes les associations pour ce type de projet
                                const deletions = record.lesson_ids
                                    .map((lessonId: number) => {
                                        const lp = lessonProjects.find(
                                            (lp) =>
                                                lp.project_type_id === record.project_type_id &&
                                                lp.lesson_id === lessonId
                                        );
                                        return lp ? lp.id : null;
                                    })
                                    .filter((id: number | null): id is number => id !== null);

                                Promise.all(deletions.map((id: number) => deleteLessonProject(id)))
                                    .then(() => {
                                        message.success(t('message.deletedSuccessfully'));
                                        return getAllLessonProjects();
                                    })
                                    .then((resp) => setLessonProjects(resp.data || []))
                                    .catch((err) => message.error(err.message));
                            }}
                        >
                            {t('button.delete')}
                        </Button>
                    </Space>
                );
            }
        }
    ];

    return (
        <AppLayout title={t('lessonProject.title')}>
            <div style={{ padding: '20px' }}>
                {/* Les sélecteurs et le bouton Save sur une seule ligne */}
                <Row gutter={16} align="middle" style={{ marginBottom: '20px' }}>
                    <Col>
                        <Select
                            style={{ width: 200 }}
                            placeholder={t('lessonProject.selectType')}
                            onChange={(value: number) => setSelectedProjectType(value)}
                            value={selectedProjectType ?? undefined}
                        >
                            {projectTypeCodifications.map((type: any) => (
                                <Option key={type.id} value={type.id}>
                                    {type.label}
                                </Option>
                            ))}
                        </Select>
                    </Col>
                    <Col>
                        <Select
                            style={{ width: 200 }}
                            placeholder={t('lessonProject.selectSubject')}
                            onChange={(value: number) => setSelectedSubject(value)}
                            value={selectedSubject ?? undefined}
                        >
                            {subjects.map((subject) => (
                                <Option key={subject.id} value={subject.id}>
                                    {subject.title}
                                </Option>
                            ))}
                        </Select>
                    </Col>
                    <Col>
                        <Select
                            style={{ width: 200 }}
                            placeholder={t('lessonProject.selectCourse')}
                            onChange={(value: number) => setSelectedCourse(value)}
                            value={selectedCourse ?? undefined}
                            disabled={!courses.length}
                        >
                            {courses.map((course) => (
                                <Option key={course.id} value={course.id}>
                                    {course.title}
                                </Option>
                            ))}
                        </Select>
                    </Col>
                    <Col>
                        <Select
                            style={{ width: 200 }}
                            placeholder={t('lessonProject.selectChapter')}
                            onChange={(value: number) => setSelectedChapter(value)}
                            value={selectedChapter ?? undefined}
                            disabled={!chapters.length}
                        >
                            {chapters.map((chapter) => (
                                <Option key={chapter.id} value={chapter.id}>
                                    {chapter.name}
                                </Option>
                            ))}
                        </Select>
                    </Col>
                    <Col>
                        <Select
                            style={{ width: 200 }}
                            placeholder={t('lessonProject.selectEpic')}
                            onChange={(value: number) => setSelectedEpic(value)}
                            value={selectedEpic ?? undefined}
                            disabled={!epics.length}
                        >
                            {epics.map((epic) => (
                                <Option key={epic.id} value={epic.id}>
                                    {epic.name}
                                </Option>
                            ))}
                        </Select>
                    </Col>
                    <Col>
                        <Select
                            mode="multiple"
                            style={{ width: 200 }}
                            placeholder={t('lessonProject.selectLessons')}
                            onChange={(value: number[]) => setSelectedLessonIds(value)}
                            value={selectedLessonIds}
                            disabled={!lessons.length}
                        >
                            {lessons.map((lesson) => (
                                <Option key={lesson.id} value={lesson.id}>
                                    {lesson.name}
                                </Option>
                            ))}
                        </Select>
                    </Col>
                    <Col>
                        <Button type="primary" onClick={handleSaveNew}>
                            {t('lessonProject.save')}
                        </Button>
                    </Col>
                </Row>

                <Spin spinning={loading}>
                    <Table<IGroupedData>
                        columns={columns}
                        dataSource={groupedData}
                        rowKey="project_type_id"
                        pagination={{ pageSize: 20 }}
                        locale={{ emptyText: t('lessonProject.noData') }}
                    />
                </Spin>
            </div>
        </AppLayout>
    );
};

export default LessonProjectList;
